/**
 * 會員管理 API 服務
 * 使用 RTK Query 封裝會員相關的 API 調用
 * 
 * 注意：目前使用 mock 數據進行開發測試
 * 待 API 整合後，將 mockBaseQuery 替換為 baseQuery
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from '@/services/baseApi';
import { IApiResponse, IPaginatedResponse } from '@/types/api';
import {
  IMember,
  IMemberListParams,
  IMemberFormData,
  IBatchUpdateMembersRequest,
  IBatchUpdateMembersResponse,
  IMemberStatistics,
  IUpdateMemberPointsRequest,
  IUpdateMemberLevelRequest,
  IMemberTag,
  IMemberTagFormData,
  IMemberTagListParams,
  IMemberTagOperationRequest,
  IBatchMemberTagOperationRequest,
  IMemberLevelConfig,
  IMemberLevelConfigFormData,
  IMemberLevelUpgradeRule,
  IMemberLevelUpgradeRuleFormData,
  IMemberLevelBenefit,
  IMemberLevelHistory,
  IMemberLevelHistoryListParams,
  IMemberLevelStatistics,
} from '@/types/member';
import { ConstantUtil } from '@/utils/ConstantUtil';

/**
 * 會員 API 服務
 */
export const memberApi = createApi({
  reducerPath: 'memberApi',
  baseQuery: baseQueryWithErrorHandling,
  // baseQuery, // 真實 API baseQuery（待 API 整合後使用）
  tagTypes: ['Member', 'MemberStatistics', 'MemberTag', 'MemberLevelConfig', 'MemberLevelUpgradeRule', 'MemberLevelBenefit', 'MemberLevelHistory', 'MemberLevelStatistics'],
  endpoints: (builder) => ({
    // 獲取會員列表
    getMembers: builder.query<IApiResponse<IPaginatedResponse<IMember>>, IMemberListParams>({
      query: (params) => ({
        url: ConstantUtil.API_MEMBERS,
        method: 'GET',
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
          ...(params.searchKeyword && { searchKeyword: params.searchKeyword }),
          ...(params.search && { search: params.search }),
          ...(params.status && params.status !== 'all' && { status: params.status }),
          ...(params.level && params.level !== 'all' && { level: params.level }),
          ...(params.membershipLevel && params.membershipLevel !== 'all' && { membershipLevel: params.membershipLevel }),
          ...(params.tagId && { tagId: params.tagId }),
          ...(params.minSpent !== undefined && { minSpent: params.minSpent }),
          ...(params.maxSpent !== undefined && { maxSpent: params.maxSpent }),
          ...(params.joinDateFrom && { joinDateFrom: params.joinDateFrom }),
          ...(params.joinDateTo && { joinDateTo: params.joinDateTo }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
        },
      }),
      providesTags: ['Member'],
    }),

    // 獲取會員詳情
    getMemberById: builder.query<IApiResponse<IMember>, string>({
      query: (id) => ({
        url: ConstantUtil.API_MEMBER_BY_ID(id),
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Member', id }],
    }),

    // 更新會員資料
    updateMember: builder.mutation<IApiResponse<IMember>, { id: string; data: Partial<IMemberFormData> }>({
      query: ({ id, data }) => ({
        url: ConstantUtil.API_MEMBER_UPDATE(id),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Member', id },
        { type: 'Member' },
        { type: 'MemberStatistics' },
      ],
    }),

    // 刪除會員（軟刪除）
    deleteMember: builder.mutation<IApiResponse<void>, string>({
      query: (id) => ({
        url: ConstantUtil.API_MEMBER_DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Member', id },
        { type: 'Member' },
        { type: 'MemberStatistics' },
      ],
    }),

    // 恢復已刪除的會員
    restoreMember: builder.mutation<IApiResponse<IMember>, string>({
      query: (id) => ({
        url: ConstantUtil.API_MEMBER_RESTORE(id),
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Member', id },
        { type: 'Member' },
        { type: 'MemberStatistics' },
      ],
    }),

    // 將會員加入黑名單
    addToBlacklist: builder.mutation<IApiResponse<IMember>, string>({
      query: (id) => ({
        url: ConstantUtil.API_MEMBER_BLACKLIST_ADD(id),
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Member', id },
        { type: 'Member' },
        { type: 'MemberStatistics' },
      ],
    }),

    // 將會員從黑名單移除
    removeFromBlacklist: builder.mutation<IApiResponse<IMember>, string>({
      query: (id) => ({
        url: ConstantUtil.API_MEMBER_BLACKLIST_REMOVE(id),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Member', id },
        { type: 'Member' },
        { type: 'MemberStatistics' },
      ],
    }),

    // 更新會員積分
    updateMemberPoints: builder.mutation<IApiResponse<IMember>, { id: string; data: IUpdateMemberPointsRequest }>({
      query: ({ id, data }) => ({
        url: ConstantUtil.API_MEMBER_UPDATE_POINTS(id),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Member', id },
        { type: 'Member' },
      ],
    }),

    // 更新會員等級
    updateMemberLevel: builder.mutation<IApiResponse<IMember>, { id: string; data: IUpdateMemberLevelRequest }>({
      query: ({ id, data }) => ({
        url: ConstantUtil.API_MEMBER_UPDATE_LEVEL(id),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Member', id },
        { type: 'Member' },
        { type: 'MemberStatistics' },
      ],
    }),

    // 批量更新會員
    batchUpdateMembers: builder.mutation<IApiResponse<IBatchUpdateMembersResponse>, IBatchUpdateMembersRequest>({
      query: (data) => ({
        url: ConstantUtil.API_MEMBERS, // 批量操作可能需要單獨的端點，這裡暫時使用列表端點
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Member', 'MemberStatistics'],
    }),

    // 獲取會員統計數據
    getMemberStatistics: builder.query<IApiResponse<IMemberStatistics>, void>({
      query: () => ({
        url: `${ConstantUtil.API_MEMBERS}/statistics`, // 統計端點可能需要單獨定義
        method: 'GET',
      }),
      providesTags: ['MemberStatistics'],
    }),

    // ========== 標籤管理相關 API ==========
    
    // 獲取標籤列表
    getMemberTags: builder.query<IApiResponse<IPaginatedResponse<IMemberTag>>, IMemberTagListParams>({
      query: (params) => ({
        url: ConstantUtil.API_MEMBER_TAGS,
        method: 'GET',
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
          ...(params.search && { search: params.search }),
          ...(params.category && params.category !== 'all' && { category: params.category }),
          ...(params.isAutoTag !== undefined && params.isAutoTag !== 'all' && { isAutoTag: params.isAutoTag }),
        },
      }),
      providesTags: ['MemberTag'],
    }),

    // 獲取標籤詳情
    getMemberTagById: builder.query<IApiResponse<IMemberTag>, string>({
      query: (id) => ({
        url: ConstantUtil.API_MEMBER_TAG_BY_ID(id),
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'MemberTag', id }],
    }),

    // 創建標籤
    createMemberTag: builder.mutation<IApiResponse<IMemberTag>, IMemberTagFormData>({
      query: (data) => ({
        url: ConstantUtil.API_MEMBER_TAG_CREATE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['MemberTag'],
    }),

    // 更新標籤
    updateMemberTag: builder.mutation<IApiResponse<IMemberTag>, { id: string; data: Partial<IMemberTagFormData> }>({
      query: ({ id, data }) => ({
        url: ConstantUtil.API_MEMBER_TAG_UPDATE(id),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'MemberTag', id },
        { type: 'MemberTag' },
        { type: 'Member' },
      ],
    }),

    // 刪除標籤
    deleteMemberTag: builder.mutation<IApiResponse<void>, string>({
      query: (id) => ({
        url: ConstantUtil.API_MEMBER_TAG_DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['MemberTag', 'Member'],
    }),

    // 為會員添加標籤
    addTagsToMember: builder.mutation<IApiResponse<IMember>, IMemberTagOperationRequest>({
      query: ({ memberId, tagIds }) => ({
        url: ConstantUtil.API_MEMBER_ADD_TAGS(memberId),
        method: 'POST',
        body: { tagIds },
      }),
      invalidatesTags: (_result, _error, { memberId }) => [
        { type: 'Member', id: memberId },
        { type: 'Member' },
      ],
    }),

    // 為會員移除標籤
    removeTagsFromMember: builder.mutation<IApiResponse<IMember>, IMemberTagOperationRequest>({
      query: ({ memberId, tagIds }) => ({
        url: ConstantUtil.API_MEMBER_REMOVE_TAGS(memberId),
        method: 'DELETE',
        body: { tagIds },
      }),
      invalidatesTags: (_result, _error, { memberId }) => [
        { type: 'Member', id: memberId },
        { type: 'Member' },
      ],
    }),

    // 批量標籤操作
    batchMemberTagOperation: builder.mutation<IApiResponse<IBatchUpdateMembersResponse>, IBatchMemberTagOperationRequest>({
      query: (data) => ({
        url: ConstantUtil.API_MEMBER_BATCH_TAG_OPERATION,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Member', 'MemberTag'],
    }),

    // 獲取標籤的會員列表
    getTagMembers: builder.query<IApiResponse<IPaginatedResponse<IMember>>, { tagId: string; params?: IMemberListParams }>({
      query: ({ tagId, params }) => ({
        url: ConstantUtil.API_MEMBER_TAG_MEMBERS(tagId),
        method: 'GET',
        params: {
          page: params?.page ?? 1,
          pageSize: params?.pageSize ?? ConstantUtil.PAGE_SIZE,
          ...(params?.searchKeyword && { searchKeyword: params.searchKeyword }),
          ...(params?.status && params.status !== 'all' && { status: params.status }),
        },
      }),
      providesTags: (_result, _error, { tagId }) => [{ type: 'MemberTag', id: tagId }, 'Member'],
    }),

    // ========== 會員等級配置 API ==========
    // 獲取等級配置列表
    getMemberLevelConfigs: builder.query<IApiResponse<IMemberLevelConfig[]>, void>({
      query: () => ({
        url: ConstantUtil.API_MEMBER_LEVEL_CONFIGS,
        method: 'GET',
      }),
      providesTags: ['MemberLevelConfig'],
    }),

    // 獲取單個等級配置
    getMemberLevelConfigById: builder.query<IApiResponse<IMemberLevelConfig>, string>({
      query: (id) => ({
        url: ConstantUtil.API_MEMBER_LEVEL_CONFIG_BY_ID(id),
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'MemberLevelConfig', id }],
    }),

    // 創建等級配置
    createMemberLevelConfig: builder.mutation<IApiResponse<IMemberLevelConfig>, IMemberLevelConfigFormData>({
      query: (data) => ({
        url: ConstantUtil.API_MEMBER_LEVEL_CONFIG_CREATE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['MemberLevelConfig'],
    }),

    // 更新等級配置
    updateMemberLevelConfig: builder.mutation<IApiResponse<IMemberLevelConfig>, { id: string; data: Partial<IMemberLevelConfigFormData> }>({
      query: ({ id, data }) => ({
        url: ConstantUtil.API_MEMBER_LEVEL_CONFIG_UPDATE(id),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'MemberLevelConfig', id }, 'MemberLevelConfig'],
    }),

    // 刪除等級配置
    deleteMemberLevelConfig: builder.mutation<IApiResponse<void>, string>({
      query: (id) => ({
        url: ConstantUtil.API_MEMBER_LEVEL_CONFIG_DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['MemberLevelConfig'],
    }),

    // ========== 會員等級升級規則 API ==========
    // 獲取升級規則列表
    getMemberLevelUpgradeRules: builder.query<IApiResponse<IMemberLevelUpgradeRule[]>, void>({
      query: () => ({
        url: ConstantUtil.API_MEMBER_LEVEL_UPGRADE_RULES,
        method: 'GET',
      }),
      providesTags: ['MemberLevelUpgradeRule'],
    }),

    // 獲取單個升級規則
    getMemberLevelUpgradeRuleById: builder.query<IApiResponse<IMemberLevelUpgradeRule>, string>({
      query: (id) => ({
        url: ConstantUtil.API_MEMBER_LEVEL_UPGRADE_RULE_BY_ID(id),
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'MemberLevelUpgradeRule', id }],
    }),

    // 創建升級規則
    createMemberLevelUpgradeRule: builder.mutation<IApiResponse<IMemberLevelUpgradeRule>, IMemberLevelUpgradeRuleFormData>({
      query: (data) => ({
        url: ConstantUtil.API_MEMBER_LEVEL_UPGRADE_RULE_CREATE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['MemberLevelUpgradeRule'],
    }),

    // 更新升級規則
    updateMemberLevelUpgradeRule: builder.mutation<IApiResponse<IMemberLevelUpgradeRule>, { id: string; data: Partial<IMemberLevelUpgradeRuleFormData> }>({
      query: ({ id, data }) => ({
        url: ConstantUtil.API_MEMBER_LEVEL_UPGRADE_RULE_UPDATE(id),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'MemberLevelUpgradeRule', id }, 'MemberLevelUpgradeRule'],
    }),

    // 刪除升級規則
    deleteMemberLevelUpgradeRule: builder.mutation<IApiResponse<void>, string>({
      query: (id) => ({
        url: ConstantUtil.API_MEMBER_LEVEL_UPGRADE_RULE_DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['MemberLevelUpgradeRule'],
    }),

    // ========== 會員等級權益 API ==========
    // 獲取等級權益列表
    getMemberLevelBenefits: builder.query<IApiResponse<IMemberLevelBenefit[]>, { levelId?: string }>({
      query: (params) => ({
        url: ConstantUtil.API_MEMBER_LEVEL_BENEFITS,
        method: 'GET',
        params: params.levelId ? { levelId: params.levelId } : {},
      }),
      providesTags: ['MemberLevelBenefit'],
    }),

    // 創建等級權益
    createMemberLevelBenefit: builder.mutation<IApiResponse<IMemberLevelBenefit>, Partial<IMemberLevelBenefit>>({
      query: (data) => ({
        url: ConstantUtil.API_MEMBER_LEVEL_BENEFIT_CREATE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['MemberLevelBenefit'],
    }),

    // 更新等級權益
    updateMemberLevelBenefit: builder.mutation<IApiResponse<IMemberLevelBenefit>, { id: string; data: Partial<IMemberLevelBenefit> }>({
      query: ({ id, data }) => ({
        url: ConstantUtil.API_MEMBER_LEVEL_BENEFIT_UPDATE(id),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'MemberLevelBenefit', id }, 'MemberLevelBenefit'],
    }),

    // 刪除等級權益
    deleteMemberLevelBenefit: builder.mutation<IApiResponse<void>, string>({
      query: (id) => ({
        url: ConstantUtil.API_MEMBER_LEVEL_BENEFIT_DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['MemberLevelBenefit'],
    }),

    // ========== 會員等級歷史 API ==========
    // 獲取等級變更歷史
    getMemberLevelHistory: builder.query<IApiResponse<IPaginatedResponse<IMemberLevelHistory>>, IMemberLevelHistoryListParams>({
      query: (params) => ({
        url: ConstantUtil.API_MEMBER_LEVEL_HISTORY,
        method: 'GET',
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
          ...(params.memberId && { memberId: params.memberId }),
          ...(params.fromLevel && params.fromLevel !== 'all' && { fromLevel: params.fromLevel }),
          ...(params.toLevel && params.toLevel !== 'all' && { toLevel: params.toLevel }),
          ...(params.changeReason && params.changeReason !== 'all' && { changeReason: params.changeReason }),
          ...(params.dateFrom && { dateFrom: params.dateFrom }),
          ...(params.dateTo && { dateTo: params.dateTo }),
          ...(params.search && { search: params.search }),
        },
      }),
      providesTags: ['MemberLevelHistory'],
    }),

    // ========== 會員等級統計 API ==========
    // 獲取等級統計數據
    getMemberLevelStatistics: builder.query<IApiResponse<IMemberLevelStatistics[]>, void>({
      query: () => ({
        url: ConstantUtil.API_MEMBER_LEVEL_STATISTICS,
        method: 'GET',
      }),
      providesTags: ['MemberLevelStatistics'],
    }),
  }),
});

// 導出 hooks
export const {
  useGetMembersQuery,
  useGetMemberByIdQuery,
  useUpdateMemberMutation,
  useDeleteMemberMutation,
  useRestoreMemberMutation,
  useAddToBlacklistMutation,
  useRemoveFromBlacklistMutation,
  useUpdateMemberPointsMutation,
  useUpdateMemberLevelMutation,
  useBatchUpdateMembersMutation,
  useGetMemberStatisticsQuery,
  // 標籤管理 hooks
  useGetMemberTagsQuery,
  useGetMemberTagByIdQuery,
  useCreateMemberTagMutation,
  useUpdateMemberTagMutation,
  useDeleteMemberTagMutation,
  useAddTagsToMemberMutation,
  useRemoveTagsFromMemberMutation,
  useBatchMemberTagOperationMutation,
  useGetTagMembersQuery,
  // 會員等級配置 hooks
  useGetMemberLevelConfigsQuery,
  useGetMemberLevelConfigByIdQuery,
  useCreateMemberLevelConfigMutation,
  useUpdateMemberLevelConfigMutation,
  useDeleteMemberLevelConfigMutation,
  // 會員等級升級規則 hooks
  useGetMemberLevelUpgradeRulesQuery,
  useGetMemberLevelUpgradeRuleByIdQuery,
  useCreateMemberLevelUpgradeRuleMutation,
  useUpdateMemberLevelUpgradeRuleMutation,
  useDeleteMemberLevelUpgradeRuleMutation,
  // 會員等級權益 hooks
  useGetMemberLevelBenefitsQuery,
  useCreateMemberLevelBenefitMutation,
  useUpdateMemberLevelBenefitMutation,
  useDeleteMemberLevelBenefitMutation,
  // 會員等級歷史 hooks
  useGetMemberLevelHistoryQuery,
  // 會員等級統計 hooks
  useGetMemberLevelStatisticsQuery,
} = memberApi;

